import { useEffect, useLayoutEffect, useMemo, useState } from 'react';

import {
  sendGlobalLayoutProps,
  ENVIRONMENTS,
  waitForElement,
  sendGlobalErrorProps,
} from 'cu-master-layout-prop-manager';
import { hasAuthParams, useAuth as useOidcAuth } from 'react-oidc-context';

import { authRequireServices } from '@/api';
import { APP_BASE_URL, ENVIRONMENT_ID } from '@/constants';

const env = ENVIRONMENTS[ENVIRONMENT_ID];

export function useAuth() {
  const [hasTriedSignin, setHasTriedSignin] = useState(false);
  const auth = useOidcAuth();
  const token = auth.user?.access_token;
  const globalLayoutProps = useMemo(
    () => ({
      token: token ?? '',
      env,
      activeLink: APP_BASE_URL,
    }),
    [token],
  );

  // automatically sign in
  useEffect(() => {
    if (
      !hasAuthParams() &&
      !auth.isAuthenticated &&
      !auth.activeNavigator &&
      !auth.isLoading &&
      !hasTriedSignin
    ) {
      void auth.signinRedirect();
      setHasTriedSignin(true);

      // set Master Layout props
      sendGlobalLayoutProps(globalLayoutProps);
    }
  }, [auth, globalLayoutProps, hasTriedSignin]);

  // refresh token when expired
  useEffect(() => {
    if (!hasAuthParams() && auth.isAuthenticated && auth.user?.expired) {
      void auth.signinSilent();
    }
  }, [auth, auth.user?.expired]);

  // setting request Authorization header for api clients on token update
  useLayoutEffect(() => {
    const interceptors = authRequireServices.map((service) =>
      service.interceptors.request.use((config) => {
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
      }),
    );

    return () => {
      authRequireServices.forEach((service, index) => {
        service.interceptors.request.eject(interceptors[index]);
      });
    };
  }, [token]);

  if (auth.isLoading) {
    return auth;
  }

  if (auth.error) {
    console.error(
      'Auth error. Please check the defined auth credentials',
      auth.error,
    );
    sendGlobalErrorProps({ errorType: 'generalError' });
  }

  if (!auth.isAuthenticated) {
    return auth;
  }

  if (auth.isAuthenticated && token) {
    // add signout callback function to Logout button
    waitForElement('#master_layout_logout_btn')
      .then((element) => {
        element.removeAttribute('href');
        element.addEventListener('click', () => {
          void auth.signoutRedirect({
            id_token_hint: auth.user?.id_token,
          });
        });
      })
      .catch(console.error);

    // set Master Layout props
    sendGlobalLayoutProps(globalLayoutProps);
  }

  return auth;
}
